import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { 
  AVAILABLE_WIDGETS, 
  WIDGET_CATEGORIES, 
  WidgetCategory,
  UserWidget,
  getWidgetConfig
} from '@/config/dashboardWidgets';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';


interface AddWidgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddWidget: (type: string, size: 'full' | 'half' | 'third') => void;
  currentWidgets: UserWidget[];
}

export function AddWidgetDialog({ 
  open, 
  onOpenChange, 
  onAddWidget,
  currentWidgets 
}: AddWidgetDialogProps) {
  const [activeCategory, setActiveCategory] = useState<WidgetCategory>('financial');
  const { isMaster, canAccess } = usePermissions();

  const categories = Object.entries(WIDGET_CATEGORIES) as [WidgetCategory, { name: string; icon: any }][];
  
  const filteredWidgets = AVAILABLE_WIDGETS.filter(widget => {
    if (widget.category !== activeCategory) return false;
    // Master tem acesso irrestrito; demais cargos verificam a feature key do widget
    if (widget.requiredPermission && !isMaster && !canAccess(widget.requiredPermission)) return false;
    return true;
  });

  const isWidgetAdded = (type: string) => {
    return currentWidgets.some(w => w.type === type);
  };

  const handleAdd = (type: string) => {
    const config = getWidgetConfig(type);
    onAddWidget(type, config?.defaultSize || 'full');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Adicionar Indicador</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as WidgetCategory)} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-4 lg:grid-cols-7 h-auto gap-1 p-1">
            {categories.map(([key, { name, icon: Icon }]) => (
              <TabsTrigger 
                key={key} 
                value={key}
                className="flex flex-col items-center gap-1 py-2 px-2 text-xs"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{name}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          
          <div className="flex-1 overflow-y-auto mt-4">
            {categories.map(([key]) => (
              <TabsContent key={key} value={key} className="mt-0 space-y-3">
                {filteredWidgets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Nenhum indicador disponível nesta categoria</p>
                  </div>
                ) : (
                  filteredWidgets.map(widget => {
                    const isAdded = isWidgetAdded(widget.type);
                    const Icon = widget.icon;
                    
                    return (
                      <div
                        key={widget.type}
                        className={cn(
                          "flex items-center justify-between p-4 rounded-lg border transition-colors",
                          isAdded 
                            ? "bg-muted/50 border-muted" 
                            : "bg-card hover:bg-accent/50 border-border"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            isAdded ? "bg-muted" : "bg-primary/10"
                          )}>
                            <Icon className={cn(
                              "h-5 w-5",
                              isAdded ? "text-muted-foreground" : "text-primary"
                            )} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className={cn(
                                "font-medium",
                                isAdded && "text-muted-foreground"
                              )}>
                                {widget.name}
                              </h4>
                              {isAdded && (
                                <Badge variant="secondary" className="text-xs">
                                  Adicionado
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {widget.description}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={isAdded ? "outline" : "default"}
                          onClick={() => handleAdd(widget.type)}
                          disabled={isAdded}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {isAdded ? 'Já adicionado' : 'Adicionar'}
                        </Button>
                      </div>
                    );
                  })
                )}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
